import axios, { AxiosResponse } from "axios";
import { Dayjs } from "dayjs";
import { API_URL } from "./api/client";
import { getSid, getToken, getAzienda, refreshToken, ensureFreshAuthContext } from "./api/gisApi";

export interface GestisciAppuntamento {
  sid?: string;
  keyStatus?: string;
  codiceSpecia?: string;
  codiceAnapre?: string;
  codiceTpgpre?: string;
  data?: string;
  ora?: string;
  medico: string;
  durata: string;
  importo: string;
  primaDisponibilita: string;
  coddiceAgenda: string;
  idPacate: string;
  idPacapr: string;
  idPacada: string;
  cognomePazientePren: string;
  nomePazientePren: string;
  telefonoPazientePren: string;
  idAnapaz: string;
  codicePostic: string;
  numeroImpegnativa: string;
  dataImpegnativa: string;
  flSuggerita: string;
  distretto: string;
  codiceClsprt: string;
  codiceIUP: string;
  idEnte: string;
  noteAppuntamento: string;
  diagnosiAppuntamento: string;
  notePrenot: string;
  flagSpostamento: string;
}

export interface CommonFieldsData {
  tipoAgenda?: string;
  tipodoc?: string;
  specialita?: string;
  prestazione?: string;
  listino?: string;
  data?: string;
  medico?: string | string[];
  medici?: string[];
  dataInizio?: string;
  dataFine?: string;
  giorni?: number;
  filtro?: string;
  applicazione?: string;
  dataIni?: string;
  dataFin?: string;
  orari?: string | Date;
  ora?: string | Date;
  durata?: string;
  importo?: string;
  primaDisp?: string;
  entiPaz?: { idEnti?: string; numeroTessera?: string };
  primaDisponibilita?: string;
  agenda?: string;
  ricerca?: string;
  keyStatus?: number | string;
  idPaziente?: number | string;
  cognomePaziente?: string;
  nomePaziente?: string;
  telefonoPaziente?: number | string;
  codiceOperatore?: string;
  codiceSpecia?: string;
  idPacate?: number | string;
  idPacapr?: number | string;
  idPacada?: number | string;
  strutture?: string;
  numPren?: string;
  codFiscale?: string;
  tipoRicerca?: string;
  nominativo?: string;
  tabella?: string;
  dataNascita?: string;
  codice?: string;
  attivi?: string;
  "tipo calcolo"?: string;
  azienda?: string;
  cfPaziente?: string;
  dataNasPaziente?: string;
  tipoDate?: string;
  numPrenotazione?: string;
  impegnativa?: string;
  origine?: string;
  tipoCodSISS?: string;
  codSISS?: string;
  ricercaId?: string;
  tipoApp?: string;
  aziende?: any[];
  entiSelezionati?: any;
  originePiattaforma?: string;
  provenienza?: string;
  statoPrenotazione?: string;
  temporaneo?: boolean;
  aziendaPrenotazione?: string;
  sospeso?: string;
  daPagare?: string;
  noteAppuntamento?: string;
  dataRif?: string | null;
  filtriFissi?: [
    {
      colFixFlt?: string | null;
      fixFlt?: string | null;
    },
  ];
}

export interface DataStampaAgenda {
  aziendaPrenotazione: string;
  dataInizioElaborazione: string;
  dataFineElaborazione: string;
  oraInizioElaborazione: string;
  oraFineElaborazione: string;
  stringaGiorni: string;
  medicoInizioElaborazione: string;
  medicoFineElaborazione: string;
  riferimentoTelefonico: string;
  riferimentoRegime: string;
  mailMedici: string;
  raggrInizioElaborazione: string;
  raggrFineElaborazione: string;
  ordineStampa: string;
  tipoStampa: string;
}

export interface AppointmentsToExtract {
  id: string;
  medico: string;
  sostituto: string;
  appuntamentiDal: string;
  appuntamentiAl: string;
  specialitaCod?: string;
  specialitaDesc?: string;
  prestazioneCod?: string;
  prestazioneDesc?: string;
  data?: string;
  tipologia?: string;
}

//Interfaccia con i dati del login
export interface LoginData {
  utente: string;
  password: string;
  azienda: string;
}

export type DTOIframe = {
  sid?: string;
  tab: number;
  data: any;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor request: controlla se il contesto auth (azienda/utente) in localStorage
// è cambiato rispetto all'ultimo snapshot; in tal caso forza refreshkc + registerkc
// e aggiorna il sid nel body prima che la richiesta parta. Senza questo, le chiamate
// axios viaggerebbero col token vecchio legato alla vecchia azienda.
axiosInstance.interceptors.request.use(async (config) => {
  const tokenBefore = getToken();
  await ensureFreshAuthContext();
  const tokenAfter = getToken();
  if (tokenBefore !== tokenAfter && config.data) {
    // axios accetta config.data come oggetto o stringa: gestiamo entrambi i casi
    if (typeof config.data === "string") {
      try {
        const bodyObj = JSON.parse(config.data);
        if (bodyObj && typeof bodyObj === "object" && "sid" in bodyObj) {
          bodyObj.sid = tokenAfter;
          config.data = JSON.stringify(bodyObj);
        }
      } catch { /* body non è JSON, prosegui */ }
    } else if (typeof config.data === "object" && config.data !== null && "sid" in config.data) {
      (config.data as Record<string, unknown>).sid = tokenAfter;
    }
  }
  return config;
});

// Interceptor response: gestisce 401/403 e "Token non valido" come fetchGuard nelle accettazioni
axiosInstance.interceptors.response.use(
  (response) => {
    // Controlla se il backend ha risposto con esito KO e token non valido
    const config = response.config as any;
    if (
      response.data?.esito === "KO" &&
      response.data?.messaggio === "Token non valido" &&
      !config._retried
    ) {
      config._retried = true;
      return refreshToken().then(() => {
        if (config.data) {
          try {
            const bodyObj = JSON.parse(config.data);
            bodyObj.sid = getToken();
            config.data = JSON.stringify(bodyObj);
          } catch { /* body non è JSON */ }
        }
        return axiosInstance(config);
      });
    }
    return response;
  },
  (error) => {
    // Non ritentare richieste annullate (AbortController) o senza config
    if (!error.config || axios.isCancel(error) || error.code === "ECONNABORTED") {
      return Promise.reject(error);
    }

    const originalRequest = error.config as any;

    // Evita loop infiniti di retry
    if (originalRequest._retried) {
      return Promise.reject(error);
    }

    // Token scaduto: refresh e retry (come fetchGuard nelle accettazioni)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      originalRequest._retried = true;
      return refreshToken().then(() => {
        if (originalRequest.data) {
          try {
            const bodyObj = JSON.parse(originalRequest.data);
            bodyObj.sid = getToken();
            originalRequest.data = JSON.stringify(bodyObj);
          } catch { /* body non è JSON */ }
        }
        return axiosInstance(originalRequest);
      });
    }

    return Promise.reject(error);
  },
);

// Login rimosso — ora gestito da src/api/auth.ts (useLogin hook, endpoint /loginkc)

//Api Sedi
export const locations = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/sedi", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api listini
export const list = async (): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/listini", { sid: getSid() });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api ricerca medico
export const doctor = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medicipre",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api specialità
export const specialities = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/specialita",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api tipo agenda
export const typology = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/tpgpre",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api prestazioni
export const performance = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/prestazioni",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//API Ricerca slot
export const research = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricdispo", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api che blocca lo slot temporaneamente
export const bookSlot = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/prenotaSlot", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Api che prenota l'appuntamento
export const manageAppointment = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/gestisciAppuntamento", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const appointmentResearch = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/medapppren", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const appointmentResearchNew = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pacaric", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const patientResearch = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricercaBac", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Viene usata anche in Dati GP per le tabelle ISTCOM, TIPRAM, TPRICE, PGMEDT
export const generalResearch = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricerche",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

export const printPDF = async (
  data: CommonFieldsData,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/stampapren", {
      ...data,
      sid: getSid(),
      azienda: getAzienda(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const mediciAge = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/mediciage",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );
    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

export const panodi = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/panodi", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const medPrest = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/medprest", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const medPresl = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/medpresl", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const referti = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/documento", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const medpgm = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/medpgm", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const creaSvilTer = async (p0: {
  signal: AbortSignal;
}): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/creaSvilTer",
      {
        sid: getSid(),
      },
      {
        signal: p0.signal,
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const svilTerapia = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/svilTerapia",
      {
        ...data,
        sid: getSid(),
      },
      // {
      //   timeout: 60000,
      // }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const interrompiSvilTer = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/interrompiSviluppo", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const confPianoTerapia = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/confPianoTerapia", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const estrSospesi = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/estrSospesi", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const spostaPren = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/spostaPren", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const extractAppointments = async (
  data: any,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/extrXRiassegna", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const riassAppuntamenti = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/riassAppuntamenti", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const valPren = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/valPren", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const panoteg = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/panoteg", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const parPacaric = async (): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/parPacaric", {
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const datiPrenotazione = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/datiPrenotazione", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const datiPacapr = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/datiPacapr", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const datiPacada = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/datiPacada", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const datiPacadc = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/datiPacadc", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/* Sezione API DATI GP */

//ID APPUNTAMENTO
export const calProgPacada = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/calProgPacada", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Cod. Agenda
export const anaggpPacada = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/anaggpPacada", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Prestazione SISS
export const ricAnapres = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricanapres", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Quesito Diagnostico
export const ricGPQudia = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricGPQudia", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Metodica
export const ricGPMetod = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricGPMetod", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Distretto
export const ricGPDistr = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricGPDistr", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//PGMEDT
export const ricpgmedt = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricpgmedt", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//RIPRIPACADC
export const ripriPacadc = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ripriPacadc", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//PAMEDG (Gestione Orari Giornalieri)
export const pamedg = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pamedg", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//PAMEDGP (Orari giornalieri – Prestazioni abilitate)
export const pamedgp = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pamedgp", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Orari giornalieri – Prestazioni selezionabili per abilitazione
export const carMedpgmp = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/carMedpgmp", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Gestione degli orari fissi attivi per una data giornata per una agenda
export const pamedo = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pamedo", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Gestione delle prestazioni abilitate negli orari fissi attivi per una data giornata per una agenda
export const pamedp = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pamedp", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Api per ottenere i possibili scenari di erogazione per prestazioni definite come raggruppabili
export const paraggr = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/paraggr", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api per creare gli appuntamenti in raggruppati in base alle selezioni effettuate tramite la API PARAGGR
export const wParaggr = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/wParaggr", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api per ottenere i possibili scenari di erogazione in combinata di una prestazione
export const pacomb = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/pacomb", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api per creare gli appuntamenti in raggruppati in base alla selezione della combinata sulla modale di pacomb
export const wPacomb = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/wPacomb", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api che restituisce i parametri che possono essere mostrati in dati regionali e gli eventuali valori
export const carPagp = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/carPagp", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api per la ricerca dei codici SISS attivi per una prestazione interna, o codici interni validi per una prestazione SISS
export const ricanapres = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/ricanapres", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//Api per ottenere l’elenco delle stampe disponibili, coerentemente con il contenuto della prenotazione
export const prensta = async (
  data: CommonFieldsData,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/prensta",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api da richiamare per stampare l’agenda del medico (o di un range di medici) di un giorno / periodo.
//La request sottoindicata prevede tutte le possibili opzioni con cui la stampa può essere prodotta
export const pastma = async (
  data: DataStampaAgenda,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pastma",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api la parametrizzazione da ottenere in ingresso sul form di "Spunta prestazioni" in base all'operatore
export const parSpunte = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/parSpunte", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

// Api per le prestazione eseguite da spuntare
export const spuntaTerapie = async (data: any): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post("/spuntaTerapie", {
      ...data,
      sid: getSid(),
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/* Funzione di ricerca agende per:
 risoluzione di eventuali ambiguità (più agende del medico sostituto possono essere compatibili 
 con l’appuntamento da sostituire);
 assegnazione ad una agenda con caratteristiche diverse (es. altra tipologia). */
export const selAgeWeb = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/selAgeWeb",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

/*Api da richiamare per verificare la presenza di una agenda idonea ad accogliere la prenotazione proveniente 
da un’altra risorsa; se non ce ne sono, o se ce n’è più di una, 
l’agenda non viene valorizzata nell’array di risposta in cui vengono associati gli appuntamenti 
alle agende della nuova risorsa */
export const ctrlAgeRias = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ctrlAgeRias",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per stampare i moduli di prenotazione (Quelli ottenuti dall'api prensta)
export const stpModuli = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/stpModuli",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione della creazione / interazione della tabella db MEDOXD associata al codice e id medico
export const medboxt = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medboxt",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

export const medboxd = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medboxd",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ricevere i dati da visualizzare nel modale Box di Medint
export const ricmedbox = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricmedbox",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestione crud prestazioni abbinate ad un'agenda
export const medpgmp = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medpgmp",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestione crud prestazioni abbinate ad un'agenda
export const pgmedt = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedt",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per visualizzare il dettaglio di un'agenda
// (tipologie, giornate, orari fissi nella giornata e prestazioni abilitate negli orari)
export const pgmedtage = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedtage",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione delle tipologie di agenda associate a un programma
export const pgmedd = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedd",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione delle fasce orarie associate alla tipologia
export const pgmedg = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedg",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione degli orari fissi associati alla giornata di erogazione
export const pgmedo = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedo",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione delle prestazioni associate agli orari fissi
export const pgmedp = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedp",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per la gestione dei periodi di validità del programma; se non esistono record,
// il programma è sempre valido (all’interno del range determinato dalle data di inizio e
// fine validità del programma e dell’agenda); la presenza di record restringe la validità
// alle date o ai periodi configurati.
export const pgmedv = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmedv",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestione della tabella equip associata ad un medico
export const eqmed = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/equmed",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestione delle note aggiuntive associate ad un medico
export const mednot = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/mednot",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per gestione delle sospensioni assegnate ad una prestazioni
export const medprstgps = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medprstgps",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );
    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per la lettura delle configurazioni dei distretti di default selezionabili
export const ricPrestgpd = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricPrestgpd",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );
    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per la lettura delle configurazioni dei distretti associate ad una prestazione
export const ricMedPrstGpd = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricMedprstgpd",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );
    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per la lettura delle configurazioni dei metodi di default selezionabili
export const ricPrestgpm = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricPrestgpm",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );
    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

// Api per la lettura delle configurazioni delle metodiche associate ad una prestazione
export const ricMedprstgpm = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricMedprstgpm",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ottenere le note e le sospensioni delle agende GP, per tipologia agende
export const ricPgmtgps = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricPgmtgps",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestire la metodica associata ad prestazione
export const medprstgpm = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medprstgpm",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestire di un "distretto" associato ad prestazione
export const medprstgpd = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medprstgpd",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestire le note e le sospensioni delle agende (per l’integrazione con GP++)
export const pgmtgps = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmtgps",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ottenere le configurazioni dei quesiti diagnostici associati all’agenda per l’integrazione GP
export const ricPgmtgpq = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricPgmtgpq",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestire le configurazioni dei quesiti associati all’agenda
export const pgmtgpq = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/pgmtgpq",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ottenere i profili di quesiti diagnostici per successiva importazione in agenda GP
export const profiliQuesiti = async (
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/profiliQuesiti",
      {
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per la gestione delle note prestazione – agenda nel contesto dell’integrazione con GP++.
export const medpgmpn = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medpgmpn",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ottenere i default delle note prestazoine/agenda GP
export const defNotePrestAgenda = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/defNotePrestAgenda",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per ottenere le configurazioni delle combinate associate alla prestazione dell'agenda
export const ricMedpgmpc = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/ricMedpgmpc",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

//Api per gestire le configurazioni delle combinate associate alla prestazione dell’agenda
export const medpgmpc = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medpgmpc",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

export const medpgmgp = async (
  data: any,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    const response = await axiosInstance.post(
      "/medpgmgp",
      {
        ...data,
        sid: getSid(),
      },
      { signal },
    );

    return response;
  } catch (error: any) {
    if (error.name === "CanceledError") {
      console.log("Richiesta annullata:", error.message);
    }
    throw error;
  }
};

export default axiosInstance;
